import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import Modal from "../Modal/Modal";
import { useModal } from "../../shared/hooks/useModal";
import styled from "styled-components";
import {format} from "date-fns";
import Swal from "sweetalert2";
import infoIcon from "../../assets/icons/infoIcon.svg"

import GenericTableComponent from "../common/Table";
import {
  updateBillStatus,
  getPayments
} from "../../redux/slices/billingSlice";
import { convertToLocalCurrency } from "../../utils/currency";

const columns = [
  { id: "name", label: "Advertiser" },
  { id: "billingMonth", label: "Billing Month" },
  { id: "paidAt", label: "Payment Date" },
  { id: "amountPaid", label: "Amount Paid" },
  { id: "status", label: "Approval" },
];

const RejectedBilling = () => {
  const dispatch = useDispatch();
  const { payments, meta, loading } = useSelector(
    (state) => state.billing
  );
  //console.log(billing)
  const { showModal, hideModal, isModalOpen } = useModal();
  //console.log(meta)

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null); // Track selected ID
  const [selectedPayment, setSelectedPayment] = useState(null)
  const partyType = "ADVERTISER"
  const status = 'REJECTED'

  useEffect(() => {
    dispatch(getPayments({ page, rowsPerPage, partyType: partyType, status: status }));
  }, [dispatch, page, rowsPerPage, status]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getPayments({ page: newPage, rowsPerPage, partyType: partyType, status: status }));
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    dispatch(getPayments({ page: 0, rowsPerPage: newRowsPerPage, partyType: partyType, status: status }));
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        dispatch(getPayments({ page: 0, rowsPerPage, name: searchTerm, partyType: partyType, status: status }));
      }, 2000),
    [dispatch, rowsPerPage]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const triggerSearch = () => {
    dispatch(getPayments({ page: 0, rowsPerPage, name: search, partyType: partyType, status: status }));
  };

  const handleViewClick = (id) => {
    const payment = payments.find((item) => item.id === id);
    if (!payment) return;
    setSelectedPayment(payment);
    setSelectedId(id); // Store the selected ID
    showModal("billing"); // Open the modal
    //console.log(id);
    //dispatch(getBillingById(id));
  };

  const approveBill = () => {
    const formData = {
      id: selectedId,
      status: "APPROVED",
    };
    dispatch(updateBillStatus(formData))
      .unwrap()
      .then(() => {
        //Swal.fire("Approved!", "The bill has been approved.", "success");
        hideModal("billing");
        dispatch(getPayments({ page, rowsPerPage }));
      });
  };

  const rejectBill = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to undo this action!",
      icon: null, 
      iconHtml:
          `<img src=${infoIcon} alt="error" style="width:50px;">`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reject it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = {
          id: selectedId,
          status: "REJECTED",
        };
        dispatch(updateBillStatus(formData))
          .unwrap()
          .then(() => {
            //Swal.fire("Rejected!", "The bill has been rejected.", "success");
            hideModal("billing");
            dispatch(getPayments({ page, rowsPerPage, partyType: partyType }));
          })
          .catch((error) => {
            console.log("Error!", error.message || "An error occurred.", "error");
          });
      }
    });
  };

  //const advertisersPayments = payments.filter((payment) => payment.partyType === "ADVERTISER")

  return (
    <div>
      {loading ? (
        <div> </div>
      ) : (
        <GenericTableComponent
          columns={columns}
          data={payments}
          //filterSearch={true}
          paginate={true}
          user
          viewLink="/view-advertiser"
          editLink="/edit-advertiser"
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={handlePageChange}
          triggerSearch={triggerSearch}
          search={search}
          handleRowsPerPageChange={handleRowsPerPageChange}
          handleSearchChange={handleSearchChange}
          totalItems={meta?.itemCount || 0}
          showModal={handleViewClick}
          billingView
        />
      )}
      <Modal
        isOpen={isModalOpen("billing")}
        label="Payment details"
        handleClose={() => hideModal("billing")}
      >
        <Container>
          <div className="header">
            <div className="flex">
              <span>●</span>
              <div>
                <h3>
                  {/* {selectedPayment?.user?.firstName} {selectedPayment?.user?.lastName} */}
                  {selectedPayment?.name}
                </h3>
                <p>{selectedPayment?.partyReference}</p>
              </div>
            </div>
            <ApprovalLabel status={selectedPayment?.status}>
              <span>●</span> {selectedPayment?.status}
            </ApprovalLabel>
          </div>

          <div className="amount">
            <p>Amount:</p>
            <h4>{convertToLocalCurrency(selectedPayment?.amountPaid)}</h4>
          </div>

          <Details>
            <h4>Transaction details</h4>
            <div className="flex">
              <label htmlFor="date">Date</label>
              <p>{selectedPayment?.paidAt ? format(new Date(selectedPayment?.paidAt), "dd-MM-yyyy") : "N/A"}</p>

            </div>

            <div className="flex">
              <label htmlFor="date">Time</label>
              <p>{selectedPayment?.paidAt ? format(new Date(selectedPayment?.paidAt), "p") : "N/A"}</p>

            </div>

            <div className="flex">
              <label htmlFor="date">Advertiser's name</label>
              <p>{selectedPayment?.name}</p>
            </div>

            {/* <div className="flex">
              <label htmlFor="date">Publisher’s fee</label>
              <p>{convertToLocalCurrency(0)}</p>
            </div>

            <div className="flex">
              <label htmlFor="date">Income</label>
              <p>{convertToLocalCurrency(selectedPayment?.campaignBudget)}</p>
            </div> */}

            {selectedPayment?.proofUrl?.length > 0 && (
              <div className="flex proof-of-payment">
                <label htmlFor="proof">Proof of Payment</label>
                {selectedPayment.proofUrl.map((file, index) => (
                  <div key={index}>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={file.name}
                    >
                      {/* {file.type.startsWith("image/") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{
                          width: "100px",
                          height: "auto",
                          marginRight: "10px",
                        }}
                      />
                    ) : ( */}
                      {/* <p>{file.name} Download</p> */}
                      Download proof
                      {/* )} */}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </Details>

          {/* Proof of payment */}

          {/* {selectedPayment?.proofUrl?.length > 0 && (
            <div className="proof-of-payment">
              <p>Proof of Payment:</p>
              {billing.proofUrl.map((file, index) => (
                <div key={index}>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={file.name}
                  >
                    {file.type.startsWith("image/") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{
                          width: "100px",
                          height: "auto",
                          marginRight: "10px",
                        }}
                      />
                    ) : (
                      <span>{file.name} Download</span>
                    )}
                  </a>
                </div>
              ))}
            </div>
          )} */}

          <Footer>
            <div></div>
            <div className="link-buttons">
              <button
                className="see-more"
                onClick={rejectBill}
                disabled={
                  selectedPayment?.status === "APPROVED" ||
                  selectedPayment?.status === "REJECTED"
                }
              >
                Reject
              </button>
              <button
                className="add-campaign"
                onClick={approveBill}
                disabled={
                  selectedPayment?.status === "APPROVED" ||
                  selectedPayment?.status === "REJECTED"
                }
              >
                Approve
              </button>
            </div>
          </Footer>
        </Container>
      </Modal>
    </div>
  );
};

const Container = styled.div`
  padding: 10px;
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-radius: 4px;
    border: 0.6px solid #b9bdc7;
    width: 100%;
    height: 60px;
    .flex {
      display: flex;
      gap: 5px;
      span {
        color: #48505e;
        font-size: 35px;
      }
      h3 {
        font-size: 16px;
        color: #383e49;
        margin-top: 7px;
        text-transform: capitalize;
      }
      p {
        color: #383e49;
        font-size: 10px;
      }
    }
  }
  .amount {
    display: flex;
    gap: 0.5em;
    margin-top: 30px;
    p {
      font-size: 14px;
      color: #2b2f38;
      margin-top: 5px;
    }
    h4 {
      font-size: 24px;
      color: #2b2f38;
    }
  }
  /* .proof {
    ///display: flex;
    gap: 0.5em;
    margin-top: 20px;
    p {
      font-size: 14px;
      color: #2b2f38;
      margin-top: 5px;
    }
    h4 {
      font-size: 24px;
      color: #2b2f38;
    }
    img {
      margin-top: 5px;
      border-radius: 5px;
      //border: 1px solid #2b2f38;
      width: 100%;
      height: 150px;
      object-fit: cover;
    }
  } */
  /* .proof-of-payment {
    margin: 20px 0;
    display: flex;
    
  } */

  /* .proof-of-payment img {
    border: 1px solid #ccc;
    border-radius: 5px;
  } */
`;
const Details = styled.div`
  margin-top: 30px;
  h4 {
    font-size: 18px;
    color: #101828;
    margin-bottom: 5px;
  }
  .flex {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    label {
      font-size: 12px;
      color: #2b2f38;
    }
    p {
      font-size: 14px;
      color: #2b2f38;
    }
    a {
      font-size: 14px;
      text-decoration: none;
      color: #007bff !important;
    }

    a:hover {
      text-decoration: underline;
    }
  }
`;
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  .link-buttons {
    margin-top: 40px;
    display: flex;
    gap: 10px;
    .see-more,
    .add-campaign {
      padding: 6px 16px;
      height: 32px;
      border: 2px solid #d0d5dd;
      border-radius: 4px;
      font-size: 12px;
      background-color: transparent;
      text-decoration: none;
      cursor: pointer;
      color: #344054;
    }
    .see-more:disabled,
    .add-campaign:disabled {
      cursor: not-allowed;
      opacity: 0.7; /* Make it appear slightly faded */
    }
    .add-campaign {
      color: #fff;
      border: 1px solid #3730a3;
      background-color: #3730a3;
    }
  }
`;
const ApprovalLabel = styled.span`
  display: inline-block;
  font-size: 10px;
  padding: 10px;
  border-radius: 16px;
  font-weight: bold;
  text-align: center;
  text-transform: capitalize;
  span {
    font-size: 15px;
  }
  color: ${({ status }) =>
    status === "APPROVED"
      ? "#3730a3"
      : status === "REJECTED"
      ? "#FF2D55"
      : "#AF52DE"};

  background-color: ${({ status }) =>
    status === "APPROVED"
      ? "#ECFDF3"
      : status === "REJECTED"
      ? "#FEF3F2"
      : "rgba(186, 79, 239, 0.12)"};
`;

export default RejectedBilling;
